import { useState, useEffect } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  MaskedAccountNumber,
  formatDate,
  formatCurrency,
} from "./InsightsCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { useCitation, getCitationForField } from "./CitationContext";

export interface BankStatementExtraction {
  institution_name?: string;
  account_type?: string;
  balance?: number;
  statement_date?: string;
  joint_account_holders?: string[];
  pod_tod_beneficiaries?: string[];
  account_number?: string;
  routing_number?: string;
}

export default function BankStatementInsights({
  data,
  citations,
}: {
  data: BankStatementExtraction;
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();

  const handleFieldClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    if (citation) showCitation(citation);
  };

  const {
    institution_name,
    account_type,
    balance,
    statement_date,
    joint_account_holders,
    pod_tod_beneficiaries,
    account_number,
    routing_number,
  } = data;

  const { person } = useAppContext();

  const [decryptedAccountNumber, setDecryptedAccountNumber] = useState<string | null>(null);
  const [decryptedRoutingNumber, setDecryptedRoutingNumber] = useState<string | null>(null);
  const [isLoadingDecryption, setIsLoadingDecryption] = useState(false);

  useEffect(() => {
    if (!person?.estate_id) {
      return;
    }

    const decryptSensitiveFields = async () => {
      setIsLoadingDecryption(true);

      // Decrypt account number
      if (account_number) {
        try {
          const decrypted = await decryptPersonData(
            person.estate_id,
            account_number,
            "account_number",
          );
          setDecryptedAccountNumber(decrypted);
        } catch (error) {
          console.error("Failed to decrypt account number:", error);
          setDecryptedAccountNumber(null);
        }
      }

      // Decrypt routing number
      if (routing_number) {
        try {
          const decrypted = await decryptPersonData(
            person.estate_id,
            routing_number,
            "routing_number",
          );
          setDecryptedRoutingNumber(decrypted);
        } catch (error) {
          console.error("Failed to decrypt routing number:", error);
          setDecryptedRoutingNumber(null);
        }
      }

      setIsLoadingDecryption(false);
    };

    decryptSensitiveFields();
  }, [account_number, routing_number, person?.estate_id]);

  return (
    <div className="px-6 pb-6 space-y-4">
      {/* Account Information */}
      <InsightsCard title="Account Information" noPadding>
        <InsightsDividerList>
          {institution_name && (
            <InsightsDividerRow
              label="Institution"
              value={institution_name}
              onClick={
                getCitationForField(citations, "institution_name")
                  ? () => handleFieldClick("institution_name")
                  : undefined
              }
            />
          )}
          {account_type && (
            <InsightsDividerRow
              label="Account Type"
              value={account_type}
              onClick={
                getCitationForField(citations, "account_type")
                  ? () => handleFieldClick("account_type")
                  : undefined
              }
            />
          )}
          <InsightsDividerRow
            label="Account Number"
            value={
              <MaskedAccountNumber value={decryptedAccountNumber} isLoading={isLoadingDecryption} />
            }
            onClick={
              getCitationForField(citations, "account_number")
                ? () => handleFieldClick("account_number")
                : undefined
            }
          />
          {routing_number && (
            <InsightsDividerRow
              label="Routing Number"
              value={
                <MaskedAccountNumber
                  value={decryptedRoutingNumber}
                  isLoading={isLoadingDecryption}
                />
              }
              onClick={
                getCitationForField(citations, "routing_number")
                  ? () => handleFieldClick("routing_number")
                  : undefined
              }
            />
          )}
          {balance !== undefined && (
            <InsightsDividerRow
              label="Balance"
              value={formatCurrency(balance)}
              onClick={
                getCitationForField(citations, "balance")
                  ? () => handleFieldClick("balance")
                  : undefined
              }
            />
          )}
          {statement_date && (
            <InsightsDividerRow
              label="Statement Date"
              value={formatDate(statement_date)}
              onClick={
                getCitationForField(citations, "statement_date")
                  ? () => handleFieldClick("statement_date")
                  : undefined
              }
            />
          )}
        </InsightsDividerList>
      </InsightsCard>

      {/* Account Holders & Beneficiaries */}
      {((joint_account_holders && joint_account_holders.length > 0) ||
        (pod_tod_beneficiaries && pod_tod_beneficiaries.length > 0)) && (
        <InsightsCard title="Account Holders & Beneficiaries" noPadding>
          <InsightsDividerList>
            {joint_account_holders && joint_account_holders.length > 0 && (
              <InsightsDividerRow
                label="Joint Holders"
                value={joint_account_holders.join(", ")}
                onClick={
                  getCitationForField(citations, "joint_account_holders")
                    ? () => handleFieldClick("joint_account_holders")
                    : undefined
                }
              />
            )}
            {pod_tod_beneficiaries && pod_tod_beneficiaries.length > 0 && (
              <InsightsDividerRow
                label="POD/TOD Beneficiaries"
                value={pod_tod_beneficiaries.join(", ")}
                onClick={
                  getCitationForField(citations, "pod_tod_beneficiaries")
                    ? () => handleFieldClick("pod_tod_beneficiaries")
                    : undefined
                }
              />
            )}
          </InsightsDividerList>
        </InsightsCard>
      )}
    </div>
  );
}
